import { inject, Injectable } from '@angular/core';
import { SucceservaringenService } from './succeservaringen.service';
import { TParkourIconName } from '@parkour/ui';
import { SupportedSucceservaringType } from '../shared/model/succeservaring';
import { combineLatest, filter, map, merge, Subject, Subscription } from 'rxjs';
import { ProfielService } from '../profiel/service/profiel.service';
import { Profiel } from '../profiel/model/profiel';
import { SucceservaringDto } from 'parkour-web-app-dto';
import { NavigationStart, Router } from '@angular/router';
import { driver } from 'driver.js';
import { AnalyticsEvent } from '../analytics/analytics-event.model';
import { AnalyticsService } from '../analytics/analytics.service';
import { startopdrachtCoachMarks } from './startopdracht-coach-mark';

type Subset<T, U extends T> = U;

export type StartopdrachtType = Subset<
  SupportedSucceservaringType,
  | 'ARTIKEL_BEWAARD'
  | 'HULPLIJN_BEWAARD'
  | 'NOTITIE_BEWAARD'
  | 'TEAMLID_GEACTIVEERD'
  | 'UITNODIGING_AANVAARD'
  | 'DOEL_VOORGESTELD'
  | 'DOEL_TOEGEVOEGD'
  | 'GEBEURTENIS_VOORGESTELD'
  | 'GEBEURTENIS_TOEGEVOEGD'
  | 'EMOJI_REACTIE_DOEL_TOEGEVOEGD'
  | 'AFSPRAAK_TOEGEVOEGD'
  | 'BEELDBELAFSPRAAK_GEPLAND'
  | 'DOEL_STAP_AFGEVINKT'
  | 'GESPREK_BERICHT_VERSTUURD'
  | 'DOEL_BERICHT_VERSTUURD'
  | 'BERICHT_EMOJI_VERSTUURD'
  | 'GESPREK_ARTIKEL_GEDEELD'
  | 'GESPREK_HULPLIJN_GEDEELD'
>;

export type Startopdracht = {
  icon: TParkourIconName;
  title: string;
  body: string;
  succeservaringType: StartopdrachtType;
};

const bewaarEenArtikelOpdracht: Startopdracht = {
  title: 'Bewaar een artikel',
  body: 'Hou interessante artikels bij.',
  icon: 'bookmark',
  succeservaringType: 'ARTIKEL_BEWAARD',
};

const bewaarEenHulplijnOpdracht: Startopdracht = {
  title: 'Bewaar een hulplijn',
  body: 'Hou interessante hulplijnen bij.',
  icon: 'bookmark',
  succeservaringType: 'HULPLIJN_BEWAARD',
};

const voegEenNotitieToeOpdracht: Startopdracht = {
  title: 'Voeg een notitie toe',
  body: 'Hou interessante notities bij.',
  icon: 'bookmark',
  succeservaringType: 'NOTITIE_BEWAARD',
};

const startEenGesprekOpdracht: Startopdracht = {
  title: 'Start een gesprek',
  body: 'Laat weten dat je hier bereikbaar bent.',
  icon: 'paper-plane',
  succeservaringType: 'GESPREK_BERICHT_VERSTUURD',
};
const hulpBijEenDoelOpdracht: Startopdracht = {
  title: 'Hulp bij een doel',
  body: 'Stuur een bericht bij een doel.',
  icon: 'target',
  succeservaringType: 'DOEL_BERICHT_VERSTUURD',
};
const deelEenArtikelOpdracht: Startopdracht = {
  title: 'Deel een artikel',
  body: 'Stuur een interessant artikel door.',
  icon: 'paper-plane',
  succeservaringType: 'GESPREK_ARTIKEL_GEDEELD',
};
const deelEenHulplijnOpdracht: Startopdracht = {
  title: 'Deel een hulplijn',
  body: 'Stuur een interessante hulplijn door.',
  icon: 'paper-plane',
  succeservaringType: 'GESPREK_HULPLIJN_GEDEELD',
};
const reactieOpEenBerichtOpdracht: Startopdracht = {
  title: 'Reageer op een bericht',
  body: 'Zeg het met een emoji.',
  icon: 'smile',
  succeservaringType: 'BERICHT_EMOJI_VERSTUURD',
};

@Injectable({
  providedIn: 'root',
})
export class StartopdrachtenService {
  private readonly succeservaringenService = inject(SucceservaringenService);
  private readonly profielService = inject(ProfielService);
  private readonly router = inject(Router);
  private readonly analyticsService = inject(AnalyticsService);

  public readonly desktopProfielTabClick$ = new Subject<void>();

  private readonly jongereOpdrachten: Startopdracht[] = [
    {
      title: 'Nodig een teamlid uit',
      body: 'Vind steun bij je team.',
      icon: 'people',
      succeservaringType: 'TEAMLID_GEACTIVEERD',
    },
    {
      title: 'Voeg je eerste doel toe',
      body: 'Werk met je team aan je doelen.',
      icon: 'target',
      succeservaringType: 'DOEL_TOEGEVOEGD',
    },
    bewaarEenArtikelOpdracht,
    {
      title: 'Voeg je eerste gebeurtenis toe',
      body: 'Deel je verhaal met je team.',
      icon: 'compass',
      succeservaringType: 'GEBEURTENIS_TOEGEVOEGD',
    },
    bewaarEenHulplijnOpdracht,
    {
      title: 'Zet je eerste stap bij een doel',
      body: 'Vink een eerste stap af bij het bereiken van je doel.',
      icon: 'check',
      succeservaringType: 'DOEL_STAP_AFGEVINKT',
    },
    {
      title: 'Voeg een afspraak toe',
      body: 'Zet een herinnering in je kalender.',
      icon: 'calendar',
      succeservaringType: 'AFSPRAAK_TOEGEVOEGD',
    },
    voegEenNotitieToeOpdracht,
    startEenGesprekOpdracht,
    hulpBijEenDoelOpdracht,
    deelEenArtikelOpdracht,
    deelEenHulplijnOpdracht,
    reactieOpEenBerichtOpdracht,
  ];

  private readonly persoonlijkTeamlidOpdrachten: Startopdracht[] = [
    {
      title: 'Neem deel aan een team',
      body: 'Laat je uitnodigen in een team.',
      icon: 'people',
      succeservaringType: 'UITNODIGING_AANVAARD',
    },
    bewaarEenArtikelOpdracht,
    {
      title: 'Stel een doel voor',
      body: 'Help een jongere met een doel.',
      icon: 'target',
      succeservaringType: 'DOEL_VOORGESTELD',
    },
    bewaarEenHulplijnOpdracht,
    {
      title: 'Stel een gebeurtenis voor',
      body: 'Help een jongere met hun verhaal.',
      icon: 'compass',
      succeservaringType: 'GEBEURTENIS_VOORGESTELD',
    },
    voegEenNotitieToeOpdracht,
    {
      title: 'Reageer op een doel',
      body: 'Geef een aanmoediging bij een doel van een jongere.',
      icon: 'smile',
      succeservaringType: 'EMOJI_REACTIE_DOEL_TOEGEVOEGD',
    },
    startEenGesprekOpdracht,
    hulpBijEenDoelOpdracht,
    deelEenArtikelOpdracht,
    deelEenHulplijnOpdracht,
    reactieOpEenBerichtOpdracht,
  ];
  private readonly professioneelTeamlidOpdrachten: Startopdracht[] = [
    ...this.persoonlijkTeamlidOpdrachten,
    {
      title: 'Plan een videogesprek',
      body: 'Nodig een jongere uit om te videobellen.',
      icon: 'calendar',
      succeservaringType: 'BEELDBELAFSPRAAK_GEPLAND',
    },
  ];

  public readonly startopdrachten$ = combineLatest([
    this.succeservaringenService.succeservaringCounts,
    this.profielService.userProfielOrUndefined$,
  ]).pipe(map(([counts, profiel]) => this.getStartopdrachtenForProfiel(profiel, counts)));

  private getStartopdrachtenForProfiel(
    profiel: Profiel | undefined,
    counts: SucceservaringDto[],
  ): Startopdracht[] {
    let opdrachten: Startopdracht[] = [];
    switch (profiel?.type) {
      case 'JONGERE': {
        opdrachten = this.jongereOpdrachten;
        break;
      }
      case 'EX_JONGERE': {
        opdrachten = [];
        break;
      }
      case 'PERSOONLIJK_TEAMLID': {
        opdrachten = this.persoonlijkTeamlidOpdrachten;
        break;
      }
      case 'PROFESSIONEEL_TEAMLID': {
        opdrachten = this.professioneelTeamlidOpdrachten;
        break;
      }
    }
    return opdrachten.filter(
      (opdracht) => !counts.map((count) => count.type).includes(opdracht.succeservaringType),
    );
  }

  private isSidebarVisible(): boolean {
    const root = document.documentElement;
    const sidebarWidth = getComputedStyle(root).getPropertyValue('--app-sidebar-width').trim();
    return !(sidebarWidth === '0rem' || sidebarWidth === '0' || sidebarWidth === '0px');
  }

  public showStartOpdrachtCoachMark(startOpdracht: Startopdracht): void {
    this.analyticsService.trackEvent(
      new AnalyticsEvent(
        'succeservaringen',
        'startopdrachtClicked',
        startOpdracht.succeservaringType,
      ),
    );

    const coachMark = startopdrachtCoachMarks[startOpdracht.succeservaringType];
    const href = `/app/me/home/over/aan-de-slag?instructionReference=${coachMark.instructionReference}`;

    const elementSelector = this.isSidebarVisible()
      ? coachMark.desktopHighlightSelector
      : coachMark.highlightSelector;

    const nodeList = document.querySelectorAll(elementSelector);
    const highlightElement = nodeList[nodeList.length - 1];

    const description = `<div><p class="font-normal typo-body font-flandersArtSans">${coachMark.beschrijving}</p><p class="typo-body text-n-1000 wysiwyg pt-3"><a href="${href}">${coachMark.meerInfoLabel}</a></p></div>`;
    let subscription: Subscription | undefined = undefined;

    const coachMarksDriver = driver();
    coachMarksDriver.highlight({
      element: highlightElement,
      popover: {
        title: coachMark.titel,
        description: description,
        align: 'center',
        side: 'top',
        showButtons: ['close'],
        popoverClass: 'driverjs-theme',
        //eslint-disable-next-line @typescript-eslint/no-unused-vars
        onCloseClick: (_element, _step, opts) => {
          opts.driver.destroy();
          subscription?.unsubscribe();
        },
        //eslint-disable-next-line @typescript-eslint/no-unused-vars
        onPopoverRender: (popover, opts) => {},
      },
    });

    subscription = merge(
      this.router.events.pipe(filter((event) => event instanceof NavigationStart)),
      this.desktopProfielTabClick$,
    ).subscribe(() => {
      coachMarksDriver.destroy();
    });
  }
}
